import { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment-jalaali";
import { X, User, Calendar, Clock, CreditCard, DollarSign, Tag, Star, Phone, Flag } from "lucide-react";
import VipStaffSelector from "./VipStaffSelector";
import NormalStaffSelector from "./NormalStaffSelector";
import useAxiosAdmin from "../../lib/axiosAdmin";
import EditVipDatePickerStep from "./EditVipDatePickerStep";
import EditNormalDatePickerStep from "./EditNormalDatePickerStep";
import EditPaymentTypeStep from "./EditPaymentTypeStep";

import {
  setAppointmentsAdminAppointments,
  setFilteredAdminAppointments,
} from "../../../../redux/action/admin/appointments/action";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const EditAppointmentModal = ({ appointment, onClose, onSuccess }) => {

    const dispatch = useDispatch();
    const axiosAdmin = useAxiosAdmin();

  const [selectedService, setSelectedService] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const selectedStaffRef = useRef(selectedStaff);
  useEffect(() => { selectedStaffRef.current = selectedStaff; }, [selectedStaff]);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [paymentType, setPaymentType] = useState(null);
  const [amount, setAmount] = useState(0);
  const [user, setUser] = useState(null);

const { appointments, filtered } = useSelector(
    (state) => state.adminAppointments
  );

  // نگاشت انواع خدمات برای نمایش فارسی
  const serviceTypeMap = {
    kasht: "کاشت",
    retoosh: "روتوش",
    tarmim: "ترمیم",
    lift: "لیفت",
  };

  // مقداردهی اولیه state ها از appointment
  useEffect(() => {
    if (!appointment) return;
    setSelectedService(appointment.service);
    setSelectedType(appointment.service?.type);
    setSelectedMethod(appointment.service?.method === "vip" ? "VIP" : "عادی");
    setSelectedStaff(appointment.staff);
    setSelectedDateTime(appointment.datetime);
    setPaymentType(appointment.paymentType);
    setAmount(appointment.amount);
    setUser(appointment.user);
  }, [appointment]);


  // --- آپدیت خودکار مبلغ هنگام تغییر نوع مژه‌کار ---
  useEffect(() => {
    if (!selectedService || !selectedType) return;
    const price = selectedService[selectedMethod === "VIP" ? "vip" : "normal"]?.price || 0;
    setAmount(price);
  }, [selectedMethod, selectedService, selectedType]);

  // جلوگیری از اسکرول شدن body هنگام باز بودن مودال
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);


  const handleSave = async () => {
        // استخراج نام و نام خانوادگی از نام کامل کاربر
        const nameParts = (
        user?.name ||
        appointment.user.name ||
        ""
        ).split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // ساخت شیء درخواست با اولویت state
        const body = {
        staffInfo: {
            _id: selectedStaff?._id || appointment.staff._id,
        },
        user: {
            phone: user?.phone || appointment.user.phone,
            firstName,
            lastName,
        },
        datetime: {
            date: selectedDateTime?.date || appointment.datetime.date, // تاریخ میلادی
            time: selectedDateTime?.time || appointment.datetime.time,
        },
        service: {
            id: selectedService?._id || appointment.service._id,
            type: selectedType || appointment.service.type,
            method:
            selectedMethod === "VIP" ? "vip" : "عادی",
        },
        paymentType: paymentType ?? appointment.paymentType,
        amount: amount ?? appointment.amount,
        status: appointment.status, // وضعیت تغییر نمی‌کند
        };

        try {
        // ارسال درخواست به API
        const res = await axiosAdmin.put(
            `/api/admin/appointment/${appointment._id}`,
            body
        );

        // دریافت داده‌ی به‌روزرسانی‌شده
        const updatedAppointment = res.data.appointment;

        // به‌روزرسانی ریداکس (appointments و filtered)
        const newAppointments = appointments.map((a) =>
            a._id === appointment._id ? updatedAppointment : a
        );
        const newFiltered = filtered.map((a) =>
            a._id === appointment._id ? updatedAppointment : a
        );

        dispatch(setAppointmentsAdminAppointments(newAppointments));
        dispatch(setFilteredAdminAppointments(newFiltered));

        toast.success("قرار با موفقیت ویرایش شد");
        onSuccess?.();
        onClose?.();
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "خطای ناشناخته";
            toast.error(message);
        }
    };

  // تابع کمکی برای تبدیل تاریخ میلادی به شمسی
  const formatJalaliDate = (dateString) => {
    if (!dateString) return "——";
    try {
      // استفاده از moment-jalaali برای تبدیل
      return moment(dateString).format("jYYYY/jMM/jDD");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "تاریخ نامعتبر";
    }
  };

  // تابع برای دریافت نام فارسی نوع خدمت
  const getServiceTypeName = (typeKey) => {
    return serviceTypeMap[typeKey] || typeKey || "—"; // نمایش کلید اصلی اگر ترجمه نبود
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 overflow-hidden bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()} // جلوگیری از بسته شدن مودال با کلیک روی محتوای آن
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 my-8"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">✏️</span> {/* آیکون ویرایش */}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">ویرایش قرار</h2>
              <p className="text-purple-100 text-sm mt-0.5">اطلاعات قرار را ویرایش کنید</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group"
            aria-label="بستن مودال"
          >
            <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* Content */}
        {/* max-h-[calc(100vh-200px)] برای اطمینان از اینکه محتوا از footer فاصله دارد */}
        <div className="p-6 overflow-y-auto max-h-[calc(100vh-200px)]">
         
         
          {/* باکس نمایش اطلاعات قرار */}
          <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-500 mb-4 flex items-center gap-2">
              <Flag className="w-4 h-4" /> {/* آیکون وضعیت/جزئیات */}
              جزئیات قرار
            </h3>

            {/* grid برای چیدمان آیتم‌ها */}
            <div className="grid md:grid-cols-2 grid-cols-1 gap-4">

              {/* آیتم خدمت (ادغام شده) */}
              <InfoItem
                icon={<Tag className="w-4 h-4 text-gray-400" />}
                label="خدمت"
                value={`${appointment?.service?.name || "—"} - ${getServiceTypeName(appointment?.service?.type)} - ${appointment?.service?.method || "—"}`}
              />

              {/* آیتم نوع مژه‌کار (VIP/عادی) */}
              <InfoItem
                icon={<Star className="w-4 h-4 text-gray-400" />}
                label="نوع مژه‌کار"
                value={appointment?.service?.method === "vip" || selectedMethod === "VIP" ? "VIP" : "عادی"}
              />

              {/* آیتم مشتری (ادغام شده) */}
              <InfoItem
                icon={<User className="w-4 h-4 text-gray-400" />}
                label="مشتری"
                value={`${appointment?.user?.name || "—"} - ${appointment?.user?.phone || "—"}`}
              />

              {/* آیتم تاریخ و ساعت رزرو (با تاریخ شمسی) */}
              <InfoItem
                icon={<Calendar className="w-4 h-4 text-gray-400" />}
                label="تاریخ و ساعت رزرو"
                value={`${formatJalaliDate(appointment?.datetime?.date)} - ساعت ${appointment?.datetime?.time || "—"}`}
              />

              {/* آیتم روش پرداخت */}
              <InfoItem
                icon={<CreditCard className="w-4 h-4 text-gray-400" />}
                label="روش پرداخت"
                value={appointment?.paymentType || "—"}
              />

              {/* آیتم مبلغ */}
              <InfoItem
                icon={<DollarSign className="w-4 h-4 text-gray-400" />}
                label="مبلغ"
                value={`${((appointment?.amount || 0)).toLocaleString()} ریال`}
              />

              {/* آیتم وضعیت */}
              <InfoItem
                icon={<Flag className="w-4 h-4 text-gray-400" />}
                label="وضعیت"
                value={appointment?.status || "—"}
              />

              {/* آیتم کارمند */}
              <InfoItem
                icon={<User className="w-4 h-4 text-gray-400" />}
                label="کارمند انتخاب شده"
                value={appointment?.staff?.displayForAdmin || "—"}
              />
            </div>
          </div>


            {/* استپ - انتخاب نوع مژه‌کار (VIP / عادی) */}
            <div className="mb-6 mt-4">
              <label className="text-sm font-semibold text-slate-700 mb-2 block">نوع مژه‌کار</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedMethod("VIP")}
                  className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                    selectedMethod === "VIP"
                      ? "border-purple-600 bg-purple-50 text-purple-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-purple-300"
                  }`}
                >
                  ⭐ VIP
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedMethod("عادی")}
                  className={`px-5 py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${
                    selectedMethod === "عادی" || selectedMethod !== "VIP"
                      ? "border-green-600 bg-green-50 text-green-700"
                      : "border-slate-200 bg-white text-slate-500 hover:border-green-300"
                  }`}
                >
                  🌿 عادی
                </button>
              </div>
            </div>

            {/* استپ - انتخاب کارکن */}
            {selectedMethod === "VIP" ? (
              <VipStaffSelector
                selectedService={selectedService}
                selectedType={selectedType}
                selectedStaff={selectedStaff}
                onSelectStaff={setSelectedStaff}
              />
            ) : (
              <NormalStaffSelector
                selectedService={selectedService}
                selectedType={selectedType}
                selectedStaff={selectedStaff}
                onSelectStaff={setSelectedStaff}
              />
            )}

            {/* 🆕 استپ - ویرایش تاریخ و ساعت VIP */}
            {selectedMethod === "VIP" && (
              <EditVipDatePickerStep
                axiosAdmin={axiosAdmin}
                selectedService={selectedService}
                selectedType={selectedType}
                selectedMethod={selectedMethod}
                selectedStaff={selectedStaff}
                appointmentDateTime={appointment?.datetime}
                onDateTimeChange={(dateTimeData) => {
                  setSelectedDateTime(dateTimeData);
                }}
              />
            )}

            {/* 👇 استپ عادی */}
            {selectedMethod !== "VIP" && (
              <EditNormalDatePickerStep
                axiosAdmin={axiosAdmin}
                selectedService={selectedService}
                selectedType={selectedType}
                appointmentDateTime={appointment?.datetime}
                appointmentStaff={appointment?.staff}
                onDateTimeChange={(data) => {
                  setSelectedDateTime({
                    date: data.date,
                    jalaaliDate: data.jalaaliDate,
                    time: data.time,
                  });
                  if (!selectedStaffRef.current) {
                    setSelectedStaff(data.staff);
                  }
                }}
              />
            )}

            <EditPaymentTypeStep
                paymentType={paymentType}
                onPaymentTypeChange={setPaymentType}
            />

          {/* اینجا بعداً مراحل ویرایش فیلدهای قابل تغییر قرار می‌گیرد */}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-between sticky bottom-0">
          <div className="text-sm text-slate-600">
            {/* پیام راهنما در صورت نیاز */}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              بازگشت
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300"
            >
              ذخیره تغییرات
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// کامپوننت کمکی برای نمایش هر آیتم اطلاعات
const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
    <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
      {icon} {/* آیکن مربوط به هر آیتم */}
    </div>
    <div className="flex flex-col min-w-0"> {/* min-w-0 برای جلوگیری از سرریز شدن متن */}
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span> {/* عنوان */}
      <span className="text-sm font-medium text-gray-800 truncate">{value}</span> {/* مقدار */}
    </div>
  </div>
);

export default EditAppointmentModal;